/**
 * Тонка обгортка над window.Telegram.WebApp. Використовуємо Telegram CloudStorage
 * замість localStorage (п.27 ТЗ — не скидати вибір клієнта; CloudStorage синхронізується
 * між пристроями і надійно працює у Telegram WebView, на відміну від localStorage, який у
 * деяких клієнтах Telegram обмежений/непередбачуваний).
 */

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    start_param?: string;
    user?: { id: number; username?: string; first_name?: string; last_name?: string };
  };
  ready(): void;
  expand(): void;
  close(): void;
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  MainButton: {
    text: string;
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    setText(text: string): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
  CloudStorage: {
    getItem(key: string, cb: (err: unknown, value: string | null) => void): void;
    setItem(key: string, value: string, cb?: (err: unknown, ok: boolean) => void): void;
  };
  openTelegramLink(url: string): void;
  openLink(url: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return typeof window !== "undefined" && window.Telegram ? window.Telegram.WebApp : null;
}

export function initTelegram(): void {
  const wa = getTelegramWebApp();
  if (!wa) return; // dev-режим у звичайному браузері — просто нема Telegram-обгортки
  wa.ready();
  wa.expand();
}

/** Deep link (п.23 ТЗ): або з query-параметра `start` (кнопка бота), або з офіційного
 * Telegram start_param (посилання виду t.me/bot/app?startapp=...). */
export function getDeepLinkPayload(): string | null {
  const fromQuery = new URLSearchParams(window.location.search).get("start");
  if (fromQuery) return fromQuery;
  const wa = getTelegramWebApp();
  return wa?.initDataUnsafe.start_param ?? null;
}

export function getInitialScreen(): string | null {
  return new URLSearchParams(window.location.search).get("screen");
}

/** Стабільний числовий псевдо-ID користувача поза Telegram (локальна розробка в звичайному браузері). */
function getDevUserId(): number {
  let devId = sessionStorage.getItem("dev_telegram_user_id");
  if (!devId) {
    devId = String(900_000_000 + Math.floor(Math.random() * 90_000_000));
    sessionStorage.setItem("dev_telegram_user_id", devId);
  }
  return Number(devId);
}

export function getTelegramUserId(): string {
  const wa = getTelegramWebApp();
  const id = wa?.initDataUnsafe.user?.id;
  return id ? String(id) : String(getDevUserId());
}

export function getTelegramUsername(): string | null {
  return getTelegramWebApp()?.initDataUnsafe.user?.username ?? null;
}

/**
 * X-Telegram-Init-Data для API-запитів. У реальному Telegram — справжній підписаний
 * initData (перевіряється на бекенді через HMAC, див. apps/api middleware/telegramAuth.ts).
 * Поза Telegram (локальна розробка у браузері) — синтетичний непідписаний рядок з тим самим
 * псевдо-ID, який приймає лише dev-режим бекенда (без BOT_TOKEN). У продакшн-режимі бекенд
 * вимагає дійсний HMAC-підпис, тому підробити заявку так не вийде.
 */
export function getInitDataHeader(): string {
  const wa = getTelegramWebApp();
  if (wa) return wa.initData;
  const devUser = { id: getDevUserId(), first_name: "Dev" };
  return `user=${encodeURIComponent(JSON.stringify(devUser))}&auth_date=${Math.floor(Date.now() / 1000)}&hash=dev`;
}

/** CloudStorage з фолбеком на localStorage поза Telegram (для локальної розробки у браузері). */
export const cloudStorage = {
  get(key: string): Promise<string | null> {
    const wa = getTelegramWebApp();
    if (!wa) return Promise.resolve(localStorage.getItem(key));
    return new Promise((resolve) => {
      wa.CloudStorage.getItem(key, (_err, value) => resolve(value ?? null));
    });
  },
  set(key: string, value: string): void {
    const wa = getTelegramWebApp();
    if (!wa) {
      localStorage.setItem(key, value);
      return;
    }
    wa.CloudStorage.setItem(key, value);
  },
};
